import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Icon } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";
import type { PaginationProps, PaginationSize } from "./types";

const ChevronLeft = ({ size }: { size: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = ({ size }: { size: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export const Pagination: React.FC<PaginationProps & ThemeOverrideProps> = ({
  size = "md",
  currentPage = 1,
  totalPages = 1,
  siblingCount = 1,
  onPageChange,
  disabled = false,
  stretched = false,
  showNavigation = true,
  backgroundColor,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  const fontSizeMap: Record<PaginationSize, number> = {
    sm: 12,
    md: 14,
    lg: 16,
  };

  const iconSizeMap: Record<PaginationSize, number> = {
    sm: 16,
    md: 18,
    lg: 20,
  };

  const itemSizeMap: Record<PaginationSize, number> = {
    sm: 24,
    md: 32,
    lg: 40,
  };

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const totalNumbers = siblingCount * 2 + 5;
    const totalBlocks = totalNumbers;

    if (totalPages <= totalBlocks) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
      const rightSiblingIndex = Math.min(
        currentPage + siblingCount,
        totalPages
      );

      const shouldShowLeftEllipsis = leftSiblingIndex > 2;
      const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

      if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
        const leftItemCount = 3 + 2 * siblingCount;
        for (let i = 1; i <= leftItemCount; i++) {
          pages.push(i);
        }
        pages.push("ellipsis", totalPages);
      } else if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
        const rightItemCount = 3 + 2 * siblingCount;
        pages.push(1, "ellipsis");
        for (let i = totalPages - rightItemCount + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
        pages.push(1, "ellipsis");
        for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
          pages.push(i);
        }
        pages.push("ellipsis", totalPages);
      }
    }
    return pages;
  };

  const handlePageClick = (page: number) => {
    if (disabled || page === currentPage || page < 1 || page > totalPages) {
      return;
    }
    onPageChange?.(page);
  };

  const pageNumbers = getPageNumbers();
  const iconSize = iconSizeMap[size];
  const itemSize = itemSizeMap[size];

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      justifyContent={stretched ? "center" : "flex-start"}
      width={stretched ? "100%" : "auto"}
      gap={4}
      flexWrap="wrap"
      backgroundColor={backgroundColor}
      testID={testID}
    >
      {showNavigation && (
        <Box
          width={itemSize}
          height={itemSize}
          alignItems="center"
          justifyContent="center"
          cursor={disabled || currentPage === 1 ? "not-allowed" : "pointer"}
          opacity={disabled || currentPage === 1 ? 0.6 : 1}
          borderRadius={4}
          onPress={() => currentPage > 1 && handlePageClick(currentPage - 1)}
          hoverStyle={
            !(disabled || currentPage === 1)
              ? { backgroundColor: theme.colors.overlay.mono }
              : undefined
          }
          role="button"
          aria-label="Previous page"
        >
          <Icon size={iconSize} color={theme.colors.content.primary}>
            <ChevronLeft size={iconSize} />
          </Icon>
        </Box>
      )}

      {pageNumbers.map((page, index) => {
        if (page === "ellipsis") {
          return (
            <Box
              testID={testID}
              key={`ellipsis-${index}`}
              width={itemSize}
              height={itemSize}
              alignItems="center"
              justifyContent="center"
            >
              <Text
                color={theme.colors.content.tertiary}
                fontSize={fontSizeMap[size]}
                fontWeight="400"
              >
                ...
              </Text>
            </Box>
          );
        }

        const isActive = page === currentPage;

        return (
          <Box
            key={page}
            width={itemSize}
            height={itemSize}
            alignItems="center"
            justifyContent="center"
            borderRadius={4}
            backgroundColor={
              isActive ? theme.colors.background.brand.primary : "transparent"
            }
            cursor={disabled || isActive ? "default" : "pointer"}
            opacity={disabled ? 0.6 : 1}
            onPress={() => handlePageClick(page as number)}
            hoverStyle={
              !disabled && !isActive
                ? { backgroundColor: theme.colors.overlay.mono }
                : undefined
            }
            role="button"
            aria-label={`Go to page ${page}`}
            aria-current={isActive ? "page" : undefined}
          >
            <Text
              color={
                isActive
                  ? theme.colors.content.on.brand
                  : theme.colors.content.primary
              }
              fontSize={fontSizeMap[size]}
              fontWeight={isActive ? "600" : "400"}
            >
              {page}
            </Text>
          </Box>
        );
      })}

      {showNavigation && (
        <Box
          width={itemSize}
          height={itemSize}
          alignItems="center"
          justifyContent="center"
          cursor={
            disabled || currentPage === totalPages ? "not-allowed" : "pointer"
          }
          opacity={disabled || currentPage === totalPages ? 0.6 : 1}
          borderRadius={4}
          onPress={() =>
            currentPage < totalPages && handlePageClick(currentPage + 1)
          }
          hoverStyle={
            !(disabled || currentPage === totalPages)
              ? { backgroundColor: theme.colors.overlay.mono }
              : undefined
          }
          role="button"
          aria-label="Next page"
        >
          <Icon size={iconSize} color={theme.colors.content.primary}>
            <ChevronRight size={iconSize} />
          </Icon>
        </Box>
      )}
    </Box>
  );
};

Pagination.displayName = "Pagination";
